/** Claves de campos_obligatorios / movil_campos_config que pertenecen al bloque propietario (N-2). */
const PROPIETARIO_EXACT_KEYS = new Set([
  'propietario',
  'propietario_nombre',
  'propietario_apellidos',
  'propietario_telefono',
  'propietario_tipo_via_id',
  'propietario_tipo_via',
  'dni',
  'telefono',
  'email',
  'idioma',
  'porcentaje_propiedad',
  'domicilio',
  'domicilio_base',
  'domicilio_numero',
  'domicilio_bloque',
  'domicilio_escalera',
  'domicilio_piso',
  'domicilio_puerta',
  'domicilio_cp',
  'domicilio_poblacion',
  'domicilio_provincia',
  'domicilio_pais',
  'domicilio_tipo_via_id',
  'domicilio_tipo_via',
]);

/** campo_key obligatorio (API) → clave en state persona del modal. */
export const CAMPO_OBL_A_PERSONA = {
  propietario_nombre: 'nombre',
  propietario_apellidos: 'apellidos',
  dni: 'dni',
  telefono: 'telefono',
  propietario_telefono: 'telefono',
  email: 'email',
  domicilio: 'domicilio_base',
  domicilio_base: 'domicilio_base',
  domicilio_numero: 'numero',
  domicilio_bloque: 'bloque',
  domicilio_escalera: 'escalera',
  domicilio_piso: 'piso',
  domicilio_puerta: 'puerta',
  domicilio_cp: 'cp',
  domicilio_poblacion: 'poblacion',
  domicilio_provincia: 'provincia',
  domicilio_pais: 'pais',
  domicilio_tipo_via_id: 'tipo_via_id',
  propietario_tipo_via_id: 'tipo_via_id',
  domicilio_tipo_via: 'tipo_via',
  propietario_tipo_via: 'tipo_via',
};

export const PERSONA_DOMICILIO_KEYS = [
  'tipo_via_id', 'tipo_via', 'domicilio_base', 'numero',
  'bloque', 'escalera', 'piso', 'puerta', 'cp', 'poblacion', 'provincia', 'pais',
];

export const PERSONA_BASICA_KEYS = ['nombre', 'apellidos', 'dni', 'telefono', 'email'];

export function esCampoPropietarioConfig(campoKey) {
  const k = String(campoKey || '').trim();
  if (!k) return false;
  if (PROPIETARIO_EXACT_KEYS.has(k)) return true;
  if (k.startsWith('domicilio_')) return true;
  if (k.startsWith('propietario_')) return true;
  return false;
}

/** ¿Parece CIF/NIF empresa? (apellidos opcionales, como en móvil). */
export function docPareceCif(doc) {
  const s = String(doc || '').trim().toUpperCase().replace(/\s/g, '');
  if (!s) return false;
  if (/^[XYZ]\d{7}[A-Z]$/.test(s)) return false;
  return /^[ABCDEFGHJKLMNPQRSUVW]\d{7}[\dA-J]$/.test(s)
    || (/^[ABCDEFGHJKLMNPQRSUVW]/.test(s) && s.length >= 8 && s.length <= 10);
}

/**
 * Obligatorios de persona según campos O de Config APP (filtrados por tipo en verificar-captacion).
 * @returns {{ personaKeys: Set<string>, labelsByPersonaKey: Record<string, string> }}
 */
export function buildObligatoriosPersonaDesdeConfig(campos = []) {
  const personaKeys = new Set();
  const labelsByPersonaKey = {};

  const mark = (pk, label) => {
    if (!pk) return;
    personaKeys.add(pk);
    if (label && !labelsByPersonaKey[pk]) labelsByPersonaKey[pk] = label;
  };

  (campos || []).forEach((c) => {
    const ck = String(c?.campo_key || '').trim();
    if (!ck) return;
    const label = String(c?.campo_label || '').trim();
    if (ck === 'propietario') {
      mark('nombre', label || 'Nombre');
      mark('apellidos', 'Apellidos');
      return;
    }
    const pk = CAMPO_OBL_A_PERSONA[ck];
    if (pk) mark(pk, label || pk);
  });

  return { personaKeys, labelsByPersonaKey };
}

/** ¿Mostrar campo en UI? Obligatorio por config o ya tiene valor. */
export function debeMostrarCampoPersona(personaKey, obligPersonaKeys, persona) {
  if (obligPersonaKeys?.has?.(personaKey)) return true;
  const v = persona?.[personaKey];
  if (v == null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  return true;
}

/** Teléfono mínimo válido (≥9 dígitos). */
export function telefonoPersonaValido(telefono) {
  const digits = String(telefono || "").replace(/\D/g, "");
  return digits.length >= 9;
}

/** Persona marcada como principal (comunicacion_key) en state del bloque propietarios. */
export function resolverPersonaPrincipalDesdeState(state) {
  if (!state) return null;
  const comKey = state.comunicacionKey || "titular";
  if (comKey === "titular") {
    return { comKey, persona: state.titular, titulo: "Propietario 1" };
  }
  const mProp = /^prop:(\d+)$/.exec(comKey);
  if (mProp) {
    const i = parseInt(mProp[1], 10);
    const p = (state.propietariosExtra || [])[i];
    if (p) return { comKey, persona: p, titulo: `Propietario ${i + 2}` };
  }
  const mCont = /^cont:(\d+)$/.exec(comKey);
  if (mCont) {
    const j = parseInt(mCont[1], 10);
    const c = (state.contactosExtra || [])[j];
    if (c) return { comKey, persona: c, titulo: `Contacto ${j + 1}` };
  }
  return { comKey: "titular", persona: state.titular, titulo: "Propietario 1" };
}

export function faltaTelefonoPrincipalEnState(state) {
  const pr = resolverPersonaPrincipalDesdeState(state);
  if (!pr?.persona) return true;
  return !telefonoPersonaValido(pr.persona.telefono);
}

/** Mensaje de error si el principal no tiene teléfono; null si OK. */
export function validarTelefonoPrincipalState(state) {
  if (!state) return null;
  const pr = resolverPersonaPrincipalDesdeState(state);
  if (!pr?.persona) return "Indica quién es el contacto principal de comunicaciones.";
  if (telefonoPersonaValido(pr.persona.telefono)) return null;
  return `El contacto principal (${pr.titulo}) debe tener teléfono.`;
}

function personaCampoTieneValor(v) {
  if (v == null) return false;
  if (typeof v === "string") return v.trim() !== "";
  return true;
}

function faltantesPersonaInterno(persona, obligPersonaKeys) {
  if (!obligPersonaKeys || obligPersonaKeys.size === 0) return [];
  const out = [];
  obligPersonaKeys.forEach((k) => {
    if (k === "apellidos" && docPareceCif(persona?.dni)) return;
    if (k === "tipo_via_id" && (personaCampoTieneValor(persona?.tipo_via_id) || personaCampoTieneValor(persona?.tipo_via))) return;
    if (!personaCampoTieneValor(persona?.[k])) out.push(k);
  });
  return out;
}

/** Obligatorios de config + teléfono si es el principal marcado. */
export function faltantesPersonaConPrincipal(persona, obligPersonaKeys, esPrincipal) {
  const base = faltantesPersonaInterno(persona, obligPersonaKeys);
  if (esPrincipal && !telefonoPersonaValido(persona?.telefono) && !base.includes("telefono")) {
    base.push("telefono");
  }
  return base;
}

/** CP colado en bloque/esc/piso/puerta (Google / hidratación). */
export function sanearPartesDomicilioPersona(persona) {
  if (!persona || typeof persona !== 'object') return persona;
  const cp = String(persona.cp || '').trim();
  const out = { ...persona };
  for (const k of ['bloque', 'escalera', 'piso', 'puerta']) {
    const v = String(out[k] || '').trim();
    if (!v) continue;
    if (/^\d{5}$/.test(v) && (!cp || v === cp)) out[k] = '';
  }
  return out;
}
